import React, { useState, useRef, useEffect } from 'react';

const LoanQuoteStep1 = ({
        formType,
        onSubmitQuote,
        loanQuotationInput,
        onChangeLoanQuotationInput,
        isSubmitting,
        errors
    }) => {

    const isVatLoan = formType === 'vat_loan';
    const isCarLoan = formType === 'car_loan';

    const [companySuggestions, setCompanySuggestions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [companySearchTerm, setCompanySearchTerm] = useState("");
    const [companySearchError, setCompanySearchError] = useState(null);
    const [isUserTyping, setIsUserTyping] = useState(false);

    const [teamMembers, setTeamMembers] = useState(() => {
        return (window.lqcSettings && window.lqcSettings.teamMembers) || [
            { slug: 'i-dont-have-a-contact-yet', name: "I Don't Have A Contact Yet" },
            { slug: 'chris-macmanus', name: 'Chris MacManus' },
            { slug: 'rhian-macmanus', name: 'Rhian MacManus' },
            { slug: 'martin-britton', name: 'Martin Britton' },
            { slug: 'phil-smith', name: 'Phil Smith' },
            { slug: 'other-not-sure', name: 'Other / Not Sure' }
        ];
    });

    useEffect(() => {
        if (!window.lqcSettings || !window.lqcSettings.teamMembers) {
            fetch('/wp-json/loan-quote/v1/team-members')
                .then(res => res.ok ? res.json() : [])
                .then(data => {
                    if (Array.isArray(data) && data.length > 0) {
                        setTeamMembers(data);
                    }
                })
                .catch(() => {});
        }
    }, []);

    const companyInputRef = useRef(null);

    const fetchCompanies = async (query) => {
        if (query.length < 3) {
            setCompanySuggestions([]);
            return;
        }

        setLoading(true);
        setCompanySearchError(null);

        try {
            const response = await fetch(`/wp-json/loan-quote/v1/company-search?query=${query}`);
            
            if (!response.ok) throw new Error("Failed to fetch companies");
            
            const data = await response.json();
            setCompanySuggestions(data || []);
        } catch (err) {
            setCompanySearchError("Couldn't fetch companies. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (!isUserTyping) return;

        const delayDebounce = setTimeout(() => {
            if (companySearchTerm) fetchCompanies(companySearchTerm);
        }, 500);

        return () => clearTimeout(delayDebounce);
    }, [companySearchTerm, isUserTyping]);

    const getTitle = () => {
        if (isVatLoan) return "VAT Loan quotation";
        if (isCarLoan) return "Car Finance Loan quotation";
        if (formType === 'business_loan') return "Business Loan quotation";
        return "Asset Finance Loan quotation";
    };

    return (
        <form onSubmit={onSubmitQuote} className="needs-validation">
            <h4>{getTitle()}</h4>
            <div className="row gx-5 gy-3">
                <div className="col-md-12">
                    <label htmlFor="main-contact" className="form-label">
                        <span>Who is your main contact's full name at MacManus Asset Finance?</span>
                    </label>
                    <select
                        id="main-contact"
                        name="main-contact"
                        className="form-select"
                        value={loanQuotationInput.mainContact || ""}
                        onChange={(e) => onChangeLoanQuotationInput({...loanQuotationInput, mainContact: e.target.value})}
                    >
                        <option value="">Select from dropdown</option>
                        {teamMembers.map((member) => (
                            <option key={member.slug || member.name} value={member.name}>
                                {member.name}
                            </option>
                        ))}
                    </select>
                </div>

                {isCarLoan ? (
                    <>
                        <div className="col-md-6">
                            <label htmlFor="vehiclePrice" className="form-label">Price of car&nbsp;
                                <span aria-label="required">*</span>
                            </label>
                            <input
                                type="number"
                                id="vehiclePrice"
                                name="vehiclePrice"
                                className={`form-control ${errors.vehiclePrice ? "is-invalid" : ""}`}
                                value={loanQuotationInput.vehiclePriceString}
                                placeholder="e.g. 25000"
                                onChange={(e) => {
                                    const price = e.target.value;
                                    const dep = loanQuotationInput.depositString || '0';
                                    const fin = price && !isNaN(price) ? Math.max(0, parseFloat(price) - parseFloat(dep || 0)) : '';
                                    onChangeLoanQuotationInput({
                                        ...loanQuotationInput,
                                        vehiclePriceString: price,
                                        amountToFinanceString: fin ? String(fin) : '',
                                        loanAmountString: fin ? String(fin) : ''
                                    });
                                }}
                            />
                            {errors.vehiclePrice && <div className="invalid-feedback">{errors.vehiclePrice}</div>}
                        </div>

                        <div className="col-md-6">
                            <label htmlFor="deposit" className="form-label">Deposit&nbsp;
                                <span aria-label="required">*</span>
                            </label>
                            <input
                                type="number"
                                id="deposit"
                                name="deposit"
                                className={`form-control ${errors.deposit ? "is-invalid" : ""}`}
                                value={loanQuotationInput.depositString}
                                placeholder="0 if no deposit"
                                onChange={(e) => {
                                    const dep = e.target.value;
                                    const price = loanQuotationInput.vehiclePriceString || '0';
                                    const fin = price && !isNaN(price) ? Math.max(0, parseFloat(price) - parseFloat(dep || 0)) : '';
                                    onChangeLoanQuotationInput({
                                        ...loanQuotationInput,
                                        depositString: dep,
                                        amountToFinanceString: fin ? String(fin) : '',
                                        loanAmountString: fin ? String(fin) : ''
                                    });
                                }}
                            />
                            {errors.deposit && <div className="invalid-feedback">{errors.deposit}</div>}
                        </div>

                        <div className="col-md-6">
                            <label htmlFor="amountToFinance" className="form-label">Amount to finance:&nbsp;
                            </label>
                            <input
                                type="number"
                                id="amountToFinance"
                                name="amountToFinance"
                                className={`form-control ${errors.amountToFinance ? "is-invalid" : ""}`}
                                value={loanQuotationInput.amountToFinanceString || (loanQuotationInput.vehiclePriceString && !isNaN(loanQuotationInput.vehiclePriceString) ? String(Math.max(0, parseFloat(loanQuotationInput.vehiclePriceString || 0) - parseFloat(loanQuotationInput.depositString || 0))) : '')}
                                placeholder="Calculated automatically"
                                readOnly
                                disabled
                            />
                            {errors.amountToFinance && <div className="invalid-feedback">{errors.amountToFinance}</div>}
                        </div>

                        <div className="col-md-6">
                            <label htmlFor="loan-term" className="form-label">Over what period (years)?&nbsp;
                            </label>
                            <input
                                type="range"
                                id="loan-term"
                                name="loan-term"
                                className="form-range lqc-form-range"
                                min="0"
                                max="2"
                                step="1"
                                value={loanQuotationInput.loanTermCode}
                                onChange={(e) => onChangeLoanQuotationInput({...loanQuotationInput, loanTermCode: e.target.value})}
                            />
                            <div className="d-flex justify-content-between px-1 text-muted small">
                                <span>3 Years</span>
                                <span>4 Years</span>
                                <span>5 Years</span>
                            </div>
                        </div>

                        <div className="col-md-6">
                            <label className="form-label">
                                Balloon payment?&nbsp;
                                <span className="info-tooltip-icon" title="A balloon payment is a lump sum paid at the end of the term to reduce your regular monthly repayments.">ⓘ</span>
                            </label>
                            <div className="lqc-toggle-group" role="group" aria-label="Balloon payment options">
                                <button
                                    type="button"
                                    className={`lqc-toggle-btn ${loanQuotationInput.hasBalloon !== false ? 'active' : ''}`}
                                    onClick={() => onChangeLoanQuotationInput({ ...loanQuotationInput, hasBalloon: true })}
                                >
                                    Yes
                                </button>
                                <button
                                    type="button"
                                    className={`lqc-toggle-btn ${loanQuotationInput.hasBalloon === false ? 'active' : ''}`}
                                    onClick={() => onChangeLoanQuotationInput({ ...loanQuotationInput, hasBalloon: false })}
                                >
                                    No
                                </button>
                            </div>
                        </div>
                    </>
                ) : isVatLoan ? (
                    <>
                        <div className="col-md-6">
                            <label htmlFor="loanAmount" className="form-label">Amount to finance:&nbsp;
                                <span aria-label="required">*</span>
                            </label>
                            <input
                                type="number"
                                id="loanAmount"
                                name="loanAmount"
                                className={`form-control ${errors.loanAmount ? "is-invalid" : ""}`}
                                value={loanQuotationInput.loanAmountString}
                                placeholder="The minimum we offer is £3,000"
                                onChange={(e) => {
                                    const amountStr = e.target.value;
                                    const amountNum = parseFloat(amountStr);
                                    let newCreditRatingCode = loanQuotationInput.creditRatingCode;
                                    if (!isNaN(amountNum) && amountNum > 0) {
                                        if (amountNum < 25000) {
                                            newCreditRatingCode = "0";
                                        } else if (amountNum <= 100000) {
                                            newCreditRatingCode = "1";
                                        } else {
                                            newCreditRatingCode = "2";
                                        }
                                    }
                                    onChangeLoanQuotationInput({
                                        ...loanQuotationInput,
                                        loanAmountString: amountStr,
                                        creditRatingCode: newCreditRatingCode
                                    });
                                }}
                            />
                            {errors.loanAmount && <div className="invalid-feedback">{errors.loanAmount}</div>}
                        </div>
                        <div className="col-md-6">
                            <label className="form-label">VAT Loan Term&nbsp;
                                <span className="info-tooltip-icon" title="VAT loans have a fixed 3-month repayment term">ⓘ</span>
                            </label>
                            <div className="vat-term-locked">
                                <div className="d-flex align-items-center">
                                    <svg className="me-2" width="16" height="16" fill="currentColor" viewBox="0 0 16 16" style={{ opacity: 0.6 }}>
                                        <path d="M8 1a2 2 0 0 1 2 2v4H6V3a2 2 0 0 1 2-2zm3 6V3a3 3 0 0 0-6 0v4a2 2 0 0 0-2 2v5a2 2 0 0 0 2 2h6a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2z"/>
                                    </svg>
                                    <span>3 months</span>
                                </div>
                                <span className="locked-badge">Locked</span>
                            </div>
                        </div>
                    </>
                ) : (
                    <>
                        <div className="col-md-6">
                            <label htmlFor="loanAmount" className="form-label">Amount to finance:&nbsp;
                                <span aria-label="required">*</span>
                            </label>
                            <input
                                type="number"
                                id="loanAmount"
                                name="loanAmount"
                                className={`form-control ${errors.loanAmount ? "is-invalid" : ""}`}
                                value={loanQuotationInput.loanAmountString}
                                placeholder="The minimum we offer is £3,000"
                                onChange={(e) => onChangeLoanQuotationInput({...loanQuotationInput, loanAmountString: e.target.value})}
                            />
                            {errors.loanAmount && <div className="invalid-feedback">{errors.loanAmount}</div>}
                        </div>
                        <div className="col-md-6">
                            <label htmlFor="loan-term" className="form-label">Over what period (months)?&nbsp;
                                <span aria-label="required">*</span>
                            </label>
                            <input
                                type="range"
                                id="loan-term"
                                name="loan-term"
                                className="form-range lqc-form-range"
                                min="0"
                                max="5"
                                step="1"
                                value={loanQuotationInput.loanTermCode}
                                onChange={(e) => onChangeLoanQuotationInput({...loanQuotationInput, loanTermCode: e.target.value})}
                            />
                            <div className="d-flex justify-content-between px-1 text-muted small">
                                <span>24</span>
                                <span>36</span>
                                <span>48</span>
                                <span>60</span>
                                <span>72</span>
                                <span>84</span>
                            </div>
                        </div>
                    </>
                )}

                <div className="col-md-12">
                    <fieldset className={`form-control ${errors.company ? "is-invalid" : ""}`}>
                        <legend className="float-none w-auto px-2">Limited company or sole-trader/partner name&nbsp;
                            <span aria-label="required">*</span>
                        </legend>
                        <div className="row gx-5">
                            <div className="col-md-6" style={{ position: "relative" }}>
                                <label htmlFor="company" className="form-label">Limited company name</label>
                                <div className="position-relative">
                                    <input
                                        type="text"
                                        id="company"
                                        name="company"
                                        ref={companyInputRef}
                                        className="form-control pe-4"
                                        value={companySearchTerm}
                                        placeholder="Start typing to find your company"
                                        onChange={(e) => {
                                            setCompanySearchTerm(e.target.value);
                                            setCompanySuggestions([]);
                                            setCompanySearchError(null);
                                            onChangeLoanQuotationInput({...loanQuotationInput, selectedCompany: ""});
                                            setIsUserTyping(true);
                                        }}
                                    />
                                    {companySearchTerm && (
                                        <button
                                            type="button"
                                            className="lqc-clear-input-btn"
                                            aria-label="Clear company search"
                                            onClick={() => {
                                                setCompanySearchTerm("");
                                                setCompanySuggestions([]);
                                                setCompanySearchError(null);
                                                onChangeLoanQuotationInput({...loanQuotationInput, selectedCompany: ""});
                                                companyInputRef.current?.focus();
                                            }}
                                        >
                                            ×
                                        </button>
                                    )}
                                </div>
                                {companySuggestions.length > 0 && (
                                    <ul className="dropdown">
                                        {companySuggestions.map((c) => (
                                            <li
                                                key={c.id}
                                                onClick={() => {
                                                    setIsUserTyping(false);
                                                    setCompanySearchTerm(`${c.name} (${c.id})`);
                                                    onChangeLoanQuotationInput({...loanQuotationInput, selectedCompany: `${c.id}:${c.name}`});
                                                    setCompanySuggestions([]);
                                                    setTimeout(() => {
                                                        companyInputRef.current?.focus();
                                                    }, 0);
                                                }}
                                            >{c.name} - <span className="company-number">{c.id}</span>
                                            </li>
                                        ))}
                                    </ul>
                                )}
                                
                                {loading && <p className="loading-indicator">Searching Companies House...</p>}
                                {companySearchError && <p className="error">{companySearchError}</p>}
                            </div>
                        
                            <div className="col-md-6">
                                <label htmlFor="sole-trader" className="form-label">Sole-trader/Partner name</label>
                                <input
                                    type="text"
                                    id="sole-trader"
                                    name="sole-trader"
                                    className="form-control"
                                    value={loanQuotationInput.soleTraderInput}
                                    onChange={(e) => onChangeLoanQuotationInput({...loanQuotationInput, soleTraderInput: e.target.value})}
                                />
                            </div>
                        </div>
                    </fieldset>
                    {errors.company && <div className="invalid-feedback">{errors.company}</div>}
                </div>

                <div className="col-md-6">
                    <label htmlFor="credit-rating" className="form-label">
                        {isVatLoan ? "Select Loan Band" : "What is your credit rating?"}&nbsp;
                        <span aria-label="required">*</span>
                    </label>
                    <input
                        type="range"
                        id="credit-rating"
                        name="credit-rating"
                        className="form-range lqc-form-range"
                        min="0"
                        max="2"
                        step="1"
                        value={loanQuotationInput.creditRatingCode}
                        onChange={(e) => onChangeLoanQuotationInput({...loanQuotationInput, creditRatingCode: e.target.value})}
                    />
                    {isVatLoan ? (
                        <div className="d-flex justify-content-between px-1 text-muted small">
                            <span>Under £25K</span>
                            <span>£25K to £100K</span>
                            <span>£100K+</span>
                        </div>
                    ) : (
                        <div className="d-flex justify-content-between px-1 text-muted small">
                            <span>Excellent</span>
                            <span>Average</span>
                            <span>Poor</span>
                        </div>
                    )}
                </div>

                <div className="col-md-6">
                    <label htmlFor="contact-name" className="form-label">Contact name&nbsp;
                        <span aria-label="required">*</span>
                    </label>
                    <input
                        type="text"
                        id="contact-name"
                        name="contact-name"
                        className={`form-control ${errors.contactNameInput ? "is-invalid" : ""}`}
                        value={loanQuotationInput.contactNameInput}
                        onChange={(e) => onChangeLoanQuotationInput({...loanQuotationInput, contactNameInput: e.target.value})}
                    />
                    {errors.contactNameInput && <div className="invalid-feedback">{errors.contactNameInput}</div>}
                </div>

                <div className="col-md-6">
                    <label htmlFor="email" className="form-label">Email&nbsp;
                        <span aria-label="required">*</span>
                    </label>
                    <input
                        type="email"
                        id="email"
                        name="email"
                        className={`form-control ${errors.emailInput ? "is-invalid" : ""}`}
                        value={loanQuotationInput.emailInput}
                        onChange={(e) => onChangeLoanQuotationInput({...loanQuotationInput, emailInput: e.target.value})}
                    />
                    {errors.emailInput && <div className="invalid-feedback">{errors.emailInput}</div>}
                </div>

                <div className="col-md-6">
                    <label htmlFor="phone" className="form-label">Phone&nbsp;
                        <span aria-label="required">*</span>
                    </label>
                    <input
                        type="tel"
                        id="phone"
                        name="phone"
                        className={`form-control ${errors.phoneInput ? "is-invalid" : ""}`}
                        value={loanQuotationInput.phoneInput}
                        onChange={(e) => onChangeLoanQuotationInput({...loanQuotationInput, phoneInput: e.target.value})}
                    />
                    {errors.phoneInput && <div className="invalid-feedback">{errors.phoneInput}</div>}
                </div>

                <div className="col-md-12 d-flex justify-content-center my-4 py-2">
                    <button type="submit" className="btn btn-primary btn-quote w-100" disabled={isSubmitting}>
                        {isSubmitting ? "Getting quote..." : "Get a quote"}
                    </button>
                </div>
            </div>
        </form>
    );
}

export default LoanQuoteStep1;
